import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, Divider, Paper,
  CircularProgress, Alert, Stack,
  MenuItem, FormControl, InputLabel, Select,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HomeIcon from "@mui/icons-material/Home";
import GroupIcon from "@mui/icons-material/Group";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import FlagIcon from "@mui/icons-material/Flag";
import NotesIcon from "@mui/icons-material/Notes";
import InterestsIcon from "@mui/icons-material/Interests";
import RepeatIcon from "@mui/icons-material/Repeat";
import TopBar from "./globalComponents/topbar";
import Footer from "./globalComponents/footer";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

// ── Brand palette ───────────────────────────────────────────────────────────
const NAVY = "#0A2E8A";
const NAVY_GRADIENT = "linear-gradient(135deg,#0A2E8A,#0090D9)";
const NAVY_GRADIENT_HOVER = "linear-gradient(135deg,#0C36A4,#00A2EF)";
const NAVY_TINT = "rgba(10, 46, 138, 0.05)";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    fontSize: 14,
    bgcolor: "#FAFBFD",
    "& fieldset": { borderColor: "#E8EDF2" },
    "&:hover fieldset": { borderColor: NAVY },
    "&.Mui-focused fieldset": { borderColor: NAVY, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: NAVY },
};

// ── Form field row with icon ───────────────────────────────────────────────────
function FieldRow({ icon, children }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box sx={{ mt: 1.75, color: NAVY, flexShrink: 0 }}>{icon}</Box>
      <Box flex={1}>{children}</Box>
    </Stack>
  );
}

export default function ConnectionRequestPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [goal, setGoal] = useState("");
  const [description, setDescription] = useState("");
  const [interest, setInterest] = useState("");
  const [frequency, setFrequency] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage("");

      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id || user?._id;

      if (!user || !userId) {
        setError("User not logged in. Please log in to send a connection request.");
        return;
      }

      if (!goal.trim() || !interest.trim() || !frequency.trim()) {
        setError("Please fill in all required fields.");
        return;
      }

      const response = await axios.post(
        "https://backend-global-1vq9.onrender.com/api/connection-requests",
        {
          mentorId: id,
          menteeId: userId,
          goal: goal.trim(),
          description: description.trim(),
          areasOfInterest: interest.trim(),
          communicationFrequency: frequency.trim(),
        },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 201) {
        setSuccessMessage("Connection request sent successfully!");
        setGoal(""); setDescription(""); setInterest(""); setFrequency("");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to send connection request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#F3F6FA" }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 6, lg: 16 }, py: { xs: 3, md: 6 } }}>

        {/* Page header — hidden after successful submission */}
        {!successMessage && (
          <Box textAlign="center" mb={{ xs: 3, md: 4 }}>
            <Typography
              fontWeight={800}
              color="#0D1117"
              sx={{ fontSize: { xs: 22, sm: 28, md: 32 }, lineHeight: 1.2 }}
            >
              Send Connection Request
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Fill in the form below to connect with your chosen mentor
            </Typography>
          </Box>
        )}

        {/* Alerts — only show errors while the form is visible */}
        {!successMessage && error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: "12px", maxWidth: 680, mx: "auto" }}>
            {error}
          </Alert>
        )}

        {/* ── Form ── */}
        {!successMessage && (
          <Paper
            elevation={0}
            sx={{
              maxWidth: 680,
              mx: "auto",
              p: { xs: 2.5, sm: 4 },
              borderRadius: "18px",
              border: "1px solid #E8EDF2",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            }}
          >
            {/* Form header */}
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
              <Box sx={{ width: 4, height: 28, borderRadius: "2px", bgcolor: NAVY }} />
              <Typography fontWeight={700} fontSize={16} color="#0D1117">
                Connection Request Form
              </Typography>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={2.5}>
              <FieldRow icon={<FlagIcon sx={{ fontSize: 20 }} />}>
                <TextField
                  fullWidth
                  label="Goal *"
                  placeholder="Enter your learning goal"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  sx={fieldSx}
                />
              </FieldRow>

              <FieldRow icon={<NotesIcon sx={{ fontSize: 20 }} />}>
                <TextField
                  fullWidth
                  label="Description (optional)"
                  multiline
                  minRows={3}
                  placeholder="Describe your learning goal in more detail..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={fieldSx}
                />
              </FieldRow>

              <FieldRow icon={<InterestsIcon sx={{ fontSize: 20 }} />}>
                <TextField
                  fullWidth
                  label="Areas of Interest *"
                  multiline
                  minRows={2}
                  placeholder="e.g. Fundraising, Pitching, Startup Growth"
                  value={interest}
                  onChange={(e) => setInterest(e.target.value)}
                  sx={fieldSx}
                />
              </FieldRow>

              <FieldRow icon={<RepeatIcon sx={{ fontSize: 20 }} />}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      fontSize: 14,
                      bgcolor: "#FAFBFD",
                      "& fieldset": { borderColor: "#E8EDF2" },
                      "&:hover fieldset": { borderColor: NAVY },
                      "&.Mui-focused fieldset": { borderColor: NAVY, borderWidth: 2 },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: NAVY },
                  }}
                >
                  <InputLabel id="freq-label">Preferred Communication Frequency *</InputLabel>
                  <Select
                    labelId="freq-label"
                    label="Preferred Communication Frequency *"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                  >
                    <MenuItem value="Daily">Daily</MenuItem>
                    <MenuItem value="Weekly">Weekly</MenuItem>
                    <MenuItem value="Bi-weekly">Bi-weekly</MenuItem>
                    <MenuItem value="Monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </FieldRow>

              {/* Hint */}
              <Typography fontSize={12} color="#8A9BB0" sx={{ pl: 4 }}>
                * Required fields
              </Typography>

              <Button
                fullWidth
                variant="contained"
                endIcon={!submitting && <SendIcon sx={{ fontSize: 17 }} />}
                onClick={handleSubmit}
                disabled={submitting}
                sx={{
                  py: 1.5,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: 14,
                  textTransform: "none",
                  background: NAVY_GRADIENT,
                  boxShadow: "none",
                  "&:hover": {
                    background: NAVY_GRADIENT_HOVER,
                    boxShadow: "0 6px 20px rgba(10,46,138,0.30)",
                  },
                  "&.Mui-disabled": { opacity: 0.6 },
                }}
              >
                {submitting
                  ? <CircularProgress size={22} sx={{ color: "#fff" }} />
                  : "Send Connection Request"}
              </Button>

              {/* Cancel */}
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  py: 1.25,
                  borderRadius: "12px",
                  fontWeight: 600,
                  fontSize: 13,
                  textTransform: "none",
                  borderColor: "#E8EDF2",
                  color: "#6B7A8D",
                  "&:hover": { borderColor: "#8A9BB0", bgcolor: "#F6F8FB" },
                }}
              >
                ← Go Back
              </Button>
            </Stack>
          </Paper>
        )}

        {/* ── Success state ── */}
        {successMessage && (
          <Box sx={{ maxWidth: 560, mx: "auto" }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                border: "1px solid #E8EDF2",
                boxShadow: "0 8px 32px rgba(10,46,138,0.12)",
              }}
            >
              {/* Gradient top section */}
              <Box
                sx={{
                  background: NAVY_GRADIENT,
                  px: { xs: 3, sm: 5 },
                  py: { xs: 4, sm: 5 },
                  textAlign: "center",
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: { xs: 60, sm: 72 }, color: "#fff", mb: 1.5 }} />
                <Typography fontWeight={800} fontSize={{ xs: 20, sm: 24 }} color="#fff" mb={0.75}>
                  Request Sent Successfully!
                </Typography>
                <Typography fontSize={13} color="rgba(255,255,255,0.85)">
                  Your connection request has been sent to your mentor.
                </Typography>
              </Box>

              {/* Bottom section */}
              <Box sx={{ bgcolor: "#fff", px: { xs: 3, sm: 5 }, py: { xs: 3, sm: 4 } }}>
                {/* Notification hint */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{
                    bgcolor: "#F3F6FA",
                    borderRadius: "12px",
                    p: 2,
                    mb: 3,
                    border: "1px solid #E8EDF2",
                  }}
                >
                  <NotificationsActiveIcon sx={{ color: NAVY, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                  <Typography fontSize={13} color="#3D4B5C" lineHeight={1.55}>
                    You will be notified once the mentor accepts your request. Check your notifications for updates.
                  </Typography>
                </Stack>

                {/* Action buttons */}
                <Stack spacing={1.5}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<GroupIcon sx={{ fontSize: 17 }} />}
                    onClick={() => navigate("/mentor")}
                    sx={{
                      py: 1.4,
                      borderRadius: "12px",
                      fontWeight: 700,
                      fontSize: 14,
                      textTransform: "none",
                      background: NAVY_GRADIENT,
                      boxShadow: "none",
                      "&:hover": {
                        background: NAVY_GRADIENT_HOVER,
                        boxShadow: "0 6px 20px rgba(10,46,138,0.28)",
                      },
                    }}
                  >
                    Browse More Mentors
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<HomeIcon sx={{ fontSize: 17 }} />}
                    onClick={() => navigate("/learner-dash")}
                    sx={{
                      py: 1.4,
                      borderRadius: "12px",
                      fontWeight: 600,
                      fontSize: 14,
                      textTransform: "none",
                      borderColor: "#E8EDF2",
                      color: "#3D4B5C",
                      "&:hover": { borderColor: NAVY, color: NAVY, bgcolor: NAVY_TINT },
                    }}
                  >
                    Go to Dashboard
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      <Footer />
    </Box>
  );
}